import { Fragment, ReactNode } from 'react';
import { Area, Line } from 'recharts';
import { CurveType } from 'recharts/types/shape/Curve';
import {
  ForecastKpiViewOption,
  KPI_CI_VIEW_SUFFICES,
  KPI_VIEW_SUFFICES
} from '@blisspointmedia/bpm-types/dist/Forecasting';
import { Brand100, Brand25, Brand50, Brand70, Neutral400, Neutral500 } from '../../../utils/colors';
import { GAP_KEY, HISTORICAL_KEY } from '../forecasting-constants';

const sharedDefaultProps = {
  isAnimationActive: false,
  type: 'monotone' as CurveType
};

const defaultLineProps = {
  ...sharedDefaultProps,
  dot: false,
  strokeWidth: 3,
  stroke: Brand25
};

const defaultAreaProps = {
  ...sharedDefaultProps,
  stroke: 'transparent',
  fillOpacity: 1,
  activeDot: false,
  fill: Brand25
};

export const buildKpiPrimaryOnlyGraphs = (
  primaryBudget: number,
  kpiView: ForecastKpiViewOption
): ReactNode => (
  <>
    <Line {...defaultLineProps} dataKey={`${HISTORICAL_KEY}-${KPI_VIEW_SUFFICES[kpiView]}`} />
    <Line
      {...defaultLineProps}
      dataKey={`${GAP_KEY}-${KPI_VIEW_SUFFICES[kpiView]}`}
      opacity={0.5}
    />
    <Area {...defaultAreaProps} dataKey={`${primaryBudget}-${KPI_CI_VIEW_SUFFICES[kpiView]}`} />
    <Line
      {...defaultLineProps}
      dataKey={`${primaryBudget}-${KPI_VIEW_SUFFICES[kpiView]}`}
      stroke={Brand70}
      strokeDasharray='4'
    />
  </>
);

export const buildKpiComparisonGraphs = (
  visibleBudgets: number[],
  hoveredBudget: number | undefined,
  selectedBudget: number | undefined,
  colorMap: Record<string, string>,
  kpiView: ForecastKpiViewOption
): ReactNode => (
  <>
    <Area
      {...defaultAreaProps}
      dataKey={`${HISTORICAL_KEY}-${KPI_VIEW_SUFFICES[kpiView]}`}
      fill={Brand25}
    />
    <Area
      {...defaultAreaProps}
      dataKey={`${GAP_KEY}-${KPI_VIEW_SUFFICES[kpiView]}`}
      fill={Brand25}
      opacity={0.5}
    />
    {visibleBudgets.map(budget => {
      const isHovered = hoveredBudget && hoveredBudget === budget;
      const isShadowed = hoveredBudget && hoveredBudget !== budget;
      const isSelected = selectedBudget && selectedBudget === budget;
      const showConfidenceInterval = isHovered || isSelected;
      const lineColor = isShadowed ? Neutral400 : colorMap[budget];

      return (
        <Fragment key={`${budget}-kpi-graph`}>
          {showConfidenceInterval && (
            <Area
              {...defaultAreaProps}
              dataKey={`${hoveredBudget ?? selectedBudget}-${KPI_CI_VIEW_SUFFICES[kpiView]}`}
              fill={colorMap[budget]}
              fillOpacity={0.25}
            />
          )}
          <Line
            {...defaultLineProps}
            dataKey={`${budget}-${KPI_VIEW_SUFFICES[kpiView]}`}
            stroke={lineColor}
            strokeDasharray='4'
          />
        </Fragment>
      );
    })}
  </>
);

export const buildSpendPrimaryOnlyGraphs = (primaryBudget: number): ReactNode => (
  <>
    <Area {...defaultAreaProps} dataKey={`${HISTORICAL_KEY}-spend`} yAxisId={0} fill={Brand25} />
    <Area
      {...defaultAreaProps}
      dataKey={`${GAP_KEY}-spend`}
      yAxisId={0}
      fill={Brand25}
      opacity={0.5}
    />
    <Area
      {...defaultAreaProps}
      dataKey={`${primaryBudget}-spend`}
      yAxisId={0}
      fill={`url(#shadedStripeNeg${Brand50})`}
    />
    <Line {...defaultLineProps} dataKey={`${HISTORICAL_KEY}-cpm`} yAxisId={1} stroke={Neutral500} />
    <Line
      {...defaultLineProps}
      dataKey={`${GAP_KEY}-cpm`}
      yAxisId={1}
      stroke={Neutral500}
      opacity={0.5}
    />
    <Line
      {...defaultLineProps}
      dataKey={`${primaryBudget}-cpm`}
      yAxisId={1}
      stroke={Brand100}
      strokeDasharray='4'
    />
  </>
);

export const buildSpendComparisonGraphs = (
  visibleBudgets: number[],
  hoveredBudget: number | undefined,
  colorMap: Record<string, string>
): ReactNode => (
  <>
    <Area {...defaultAreaProps} dataKey={`${HISTORICAL_KEY}-spend`} yAxisId={0} />
    <Area {...defaultAreaProps} dataKey={`${GAP_KEY}-spend`} yAxisId={0} opacity={0.5} />
    {visibleBudgets.map(budget => {
      const isShadowed = hoveredBudget && hoveredBudget !== budget;
      const lineColor = isShadowed ? Neutral400 : colorMap[budget];

      return (
        <Line
          {...defaultLineProps}
          key={`${budget}-spend-line`}
          dataKey={`${budget}-spend`}
          yAxisId={0}
          stroke={lineColor}
          strokeDasharray='4'
        />
      );
    })}
  </>
);
